import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Button, Modal } from 'react-bootstrap';
import { GeoAlt, Gear, Building } from 'react-bootstrap-icons';
import PersonalizedNewsReport from '../feed/PersonalizedNewsReport';
import BottomNavbar from '../navigation/Menu';
import JurisdictionNewsFeed from '../local/JurisdictionNewsFeed';
import './LocalPage.css';
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';

// Only settlement-level results are useful here: the local report and the
// crawl updates feed both filter on a city name.
const VALID_CITY_TYPES = ['City', 'Town', 'Village', 'Borough', 'Township', 'Municipality', 'Locality'];

// The saved city, split rather than pre-joined. The header wants one string,
// but api2 needs the name and the state separately -- no endpoint there takes a
// city name on its own, because a name does not identify a place: eight states
// have a Springfield. So both parts are kept and the display string is built
// from them rather than being the only thing stored.
const readSavedCity = () => {
  const savedHomeCityData = localStorage.getItem('homeCityData');
  if (savedHomeCityData) {
    try {
      const cityData = JSON.parse(savedHomeCityData);
      if (cityData?.name) {
        const state = cityData.state || cityData.province || cityData.region || '';
        return {
          name: cityData.name,
          state,
          display: state ? `${cityData.name}, ${state}` : cityData.name,
        };
      }
    } catch (e) {
      // Fall through to the plain keys
    }
  }

  const name = localStorage.getItem('homeCity');
  if (!name) return null;

  const state = localStorage.getItem('homeCityState') || '';
  return {
    name,
    state,
    display: state ? `${name}, ${state}` : name,
  };
};

const LocalPage = () => {
  const [homeCity, setHomeCity] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [citySearchResults, setCitySearchResults] = useState([]);
  const [searchingCities, setSearchingCities] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    // Mobile detection
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    // The page is nothing without a city, so ask for one straight away rather
    // than sending the user off to settings and back.
    const saved = readSavedCity();
    setHomeCity(saved);
    if (!saved) setCityModalOpen(true);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const searchCities = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setCitySearchResults([]);
      setSearchError(null);
      setShowCityDropdown(false);
      return;
    }

    setSearchingCities(true);
    setSearchError(null);

    try {
      const response = await fetch(
        `https://search.govglance.org/api/geocode?search=${encodeURIComponent(query)}`
      );
      if (!response.ok) throw new Error('Failed to search cities');

      const data = await response.json();
      const filteredResults = (data.results || []).filter(
        (result) => VALID_CITY_TYPES.includes(result.type)
      );

      setCitySearchResults(filteredResults);
      setShowCityDropdown(filteredResults.length > 0);
    } catch (error) {
      console.error('City search error:', error);
      setSearchError('Failed to search cities. Please try again.');
      setCitySearchResults([]);
      setShowCityDropdown(false);
    } finally {
      setSearchingCities(false);
    }
  }, []);

  const handleCitySearchChange = useCallback((e) => {
    const query = e.target.value;
    setCitySearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => { searchCities(query); }, 300);
  }, [searchCities]);

  const closeCityModal = useCallback(() => {
    setCityModalOpen(false);
    setCitySearchQuery('');
    setCitySearchResults([]);
    setShowCityDropdown(false);
    setSearchError(null);
  }, []);

  // Writes exactly the keys PreferencesPage writes, so a city set here shows up
  // there and vice versa. The manual storage event is what usePersonalizedReport
  // listens for to regenerate on a location change.
  const handleCitySelect = useCallback((city) => {
    localStorage.setItem('homeCity', city.name);
    localStorage.setItem('homeCityState', city.state);
    localStorage.setItem('homeCityStateName', city.stateName);
    localStorage.setItem('homeCityData', JSON.stringify(city));
    if (!localStorage.getItem('homeCountry')) {
      localStorage.setItem('homeCountry', 'us');
    }

    setHomeCity({
      name: city.name,
      state: city.state,
      display: `${city.name}, ${city.state}`,
    });
    closeCityModal();
    window.dispatchEvent(new Event('storage'));
  }, [closeCityModal]);

  const handleCitySearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (citySearchResults.length > 0) handleCitySelect(citySearchResults[0]);
    }
  }, [citySearchResults, handleCitySelect]);

  // Same page on both, only the wrapper spacing differs: mobile clears the
  // navbar, desktop centers the column.
  const pageClass = isMobile
    ? 'local-page local-page-mobile'
    : 'local-page local-page-desktop';
  const wrapperClass = isMobile
    ? 'local-page-content-wrapper-mobile'
    : 'local-page-content-wrapper';

  const cityModal = (
    <Modal
      show={cityModalOpen}
      onHide={closeCityModal}
      size="lg"
      centered
      className="local-city-modal"
    >
      <Modal.Header className="local-city-modal-header" closeButton>
        <h3><Building className="me-2" />Set Your City</h3>
      </Modal.Header>
      <Modal.Body className="local-city-modal-body">
        <p className="local-city-modal-intro">
          Local news reports and government website updates are built around one city.
        </p>
        <div className="local-city-input-wrapper">
          <input
            type="text"
            value={citySearchQuery}
            onChange={handleCitySearchChange}
            onKeyDown={handleCitySearchKeyDown}
            onFocus={() => citySearchResults.length > 0 && setShowCityDropdown(true)}
            placeholder="Type your city name..."
            className={`local-city-input ${showCityDropdown ? 'dropdown-open' : ''}`}
            autoFocus
          />

          {searchingCities && <div className="local-city-loading">Searching...</div>}

          {showCityDropdown && !searchingCities && citySearchResults.length > 0 && (
            <div className="local-city-dropdown">
              {citySearchResults.map((city, index) => (
                <button
                  type="button"
                  key={`${city.name}_${city.state}_${index}`}
                  onClick={() => handleCitySelect(city)}
                  className="local-city-dropdown-item"
                >
                  <span>
                    <span style={{ fontWeight: '500' }}>{city.name}</span>
                    <span style={{ opacity: 0.7 }}>, {city.stateName}</span>
                  </span>
                  <span style={{ fontSize: '12px', opacity: 0.5 }}>{city.type}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {searchError && <div className="local-city-error">{searchError}</div>}

        <div className="local-city-helper">
          {citySearchQuery.length < 2 ? (
            <p>Start typing to search for your city</p>
          ) : !searchingCities && citySearchResults.length === 0 ? (
            <p>No cities found matching "{citySearchQuery}"</p>
          ) : null}
        </div>
      </Modal.Body>
    </Modal>
  );

  if (!homeCity) {
    return (
      <div id="homebg" style={{ paddingTop: 0 }} className={pageClass}>
        <IntegratedSearchNavBar currentCountry="us" placeholder="Search Gov Glance" onSearchChange={() => {}} searchQuery="" />
        <BottomNavbar />
        <div className={wrapperClass}>
          <Container className="local-page-container">
            <div className="local-empty-state">
              <GeoAlt size={48} className="local-empty-icon" />
              <h4 className="local-empty-title">No Home City Set</h4>
              <p className="local-empty-text">
                Set your home city to get local news reports and government website updates.
              </p>
              <Button
                onClick={() => setCityModalOpen(true)}
                className="local-set-location-btn"
              >
                <Building /> Set City
              </Button>
            </div>
          </Container>
        </div>
        {cityModal}
      </div>
    );
  }

  return (
    <div id="homebg" style={{ paddingTop: 0 }} className={pageClass}>
      <IntegratedSearchNavBar currentCountry="us" placeholder="Search Gov Glance" onSearchChange={() => {}} searchQuery="" />
      <BottomNavbar />

      {/* The navbar reserves its own clearance, so the wrapper adds none on
          mobile and only a small amount on desktop. */}
      <div className={wrapperClass}>

        {/* Local Header - matches StatePage header style */}
        <div className="local-header-mobile">
          <div className="local-header-content">
            <div className="local-header-title-section">
              <GeoAlt size={16} className="local-header-icon" />
              <h5 className="local-header-title">{homeCity.display}</h5>
            </div>
            <Button
              variant="link"
              onClick={() => setCityModalOpen(true)}
              className="local-settings-btn"
            >
              <Gear size={14} />
            </Button>
          </div>
        </div>

        {/* The report, plus the city's website updates as their own section
            below it (updatesMode="always" rather than the fallback default). */}
        <div className="local-page-column">
          <PersonalizedNewsReport
            reportType="local"
            isFirstCarousel={false}
            showOnDesktop
            updatesMode="always"
          />

          {/* Under the report rather than above it: the report is generated,
              this is the archived record it was generated from, so somebody
              who wants to check a claim can read the source on the same
              screen. Remounts on a city change so the previous city's
              headlines never sit under the new city's name. */}
          <JurisdictionNewsFeed
            key={`${homeCity.name}-${homeCity.state}`}
            city={homeCity.name}
            stateAbbr={homeCity.state}
          />
        </div>
      </div>

      {cityModal}
    </div>
  );
};

export default LocalPage;